/**
 * bloomreach-wishlist-bridge.js
 *
 * Injects a wishlist heart button into each <br-product-card> on the search page
 * and in the autosuggest overlay (available on all pages).
 *
 * HOW IT WORKS:
 * - <br-product-card> renders via Shadow DOM — light DOM children are invisible
 *   because Bloomreach's shadow root has no <slot>.
 * - Solution: wrap each <br-product-card> in a position:relative div, then append
 *   the button as a SIBLING of the card inside that wrapper (NOT inside the card).
 *
 * TWO CARD TYPES:
 * 1. Search page cards — have .br-product-card__add-to-cart with variant ID in href
 * 2. Autosuggest cards — attr data-br-autosuggest, no add-to-cart; fetch product JSON
 *
 * BUGS FOUND & FIXED DURING TESTING:
 * 1. Bloomreach recycles DOM nodes after filter re-render — checking parent classList
 *    instead of an attribute prevents double-injection on recycled nodes.
 * 2. MutationObserver fires before shadow root is initialized — retry with backoff.
 * 3. Variant ID read at click time — prevents stale ID if Bloomreach updates the
 *    add-to-cart href when user clicks a color swatch.
 */
(() => {
  const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'IMG', 'NOSCRIPT']);
  const variantCache = new Map();

  const getVariantId = (card) => {
    const shadow = card.shadowRoot;
    if (!shadow) return null;
    const el = shadow.querySelector('.br-product-card__add-to-cart');
    if (!el) return null;
    const match = (el.getAttribute('href') || '').match(/[?&]id=(\d+)/);
    return match ? match[1] : null;
  };

  const getProductHandle = (card) => {
    const shadow = card.shadowRoot;
    if (!shadow) return null;
    const link = shadow.querySelector('a.br-product-card__link');
    if (!link) return null;
    const match = (link.getAttribute('href') || '').match(/\/products\/([^?#]+)/);
    return match ? match[1] : null;
  };

  const getVariantIdForAutosuggest = async (card) => {
    const handle = getProductHandle(card);
    if (!handle) return null;
    if (variantCache.has(handle)) return variantCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`);
      const data = await res.json();
      const id = String(data.variants?.[0]?.id || '');
      if (id) variantCache.set(handle, id);
      return id || null;
    } catch {
      return null;
    }
  };

  const createHeartSVG = (filled) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', filled ? 'currentColor' : 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.75');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z');
    svg.appendChild(path);
    return svg;
  };

  const isAlreadyInWishlist = (variantId) => {
    const lists = window.headerWishlistData?.wishlists;
    if (!lists) return false;
    return lists.some(list => list.variants?.[variantId]);
  };

  const buildButton = (card, variantId) => {
    let filled = isAlreadyInWishlist(variantId);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'br-wl-btn wishlist-plp-icon-wrapper';
    btn.setAttribute('aria-label', 'Add to wishlist');
    btn.style.border = 'none';
    btn.style.padding = '0';

    const iconContainer = document.createElement('span');
    iconContainer.className = 'wishlist-icon-container wishlist-plp-icon-container';
    if (filled) iconContainer.classList.add('filled');

    const svg = createHeartSVG(filled);
    svg.classList.add('wishlist-heart-icon');

    iconContainer.appendChild(svg);
    btn.appendChild(iconContainer);

    const setFilled = (val) => {
      filled = val;
      iconContainer.classList.toggle('filled', filled);
      svg.setAttribute('fill', filled ? 'currentColor' : 'none');
    };

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const data = window.headerWishlistData;

      // FIX 3: read variant ID fresh at click time — Bloomreach may update the
      // add-to-cart href when user selects a color swatch.
      const currentVariantId = getVariantId(card) || variantId;
      const handle = getProductHandle(card);

      // Redirect to login if not logged in — mirrors native PLP icon behavior.
      // Return URL points to the product page with ?add_to_wishlist so the app
      // auto-adds the item after login (handled by initializePDPIcon).
      if (!data?.isLoggedIn) {
        const returnUrl = handle
          ? `/products/${handle}?add_to_wishlist=${currentVariantId}`
          : window.location.pathname + window.location.search;
        window.location.href = `${data.loginUrl}${encodeURIComponent(returnUrl)}${data.loginUrlSuffix}`;
        return;
      }

      if (typeof toggleWishlistItem !== 'undefined') {
        btn.style.opacity = '0.5';
        btn.style.pointerEvents = 'none';
        toggleWishlistItem(currentVariantId, data, { handle })
          .then((result) => setFilled(result.added))
          .finally(() => {
            btn.style.opacity = '';
            btn.style.pointerEvents = '';
          });
      } else {
        setFilled(!filled);
        console.log('[BR-Wishlist-Bridge] variant', currentVariantId, filled ? 'ADDED' : 'REMOVED');
      }
    });

    return btn;
  };

  const wrapAndInject = (card, variantId) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'br-wl-wrapper';
    card.parentElement.insertBefore(wrapper, card);
    wrapper.appendChild(card);
    wrapper.appendChild(buildButton(card, variantId));
  };

  const tryInject = async (card, attempt = 0) => {
    // FIX 1: check parent instead of attribute — Bloomreach recycles DOM nodes
    // after filter re-render, so an attribute set earlier persists on the node
    // even after Bloomreach moves it back out of our wrapper.
    if (card.parentElement?.classList.contains('br-wl-wrapper')) return;

    if (card.hasAttribute('data-br-autosuggest')) {
      // Autosuggest card: no add-to-cart element; fetch default variant from product JSON.
      const variantId = await getVariantIdForAutosuggest(card);
      if (variantId && !card.parentElement?.classList.contains('br-wl-wrapper')) {
        wrapAndInject(card, variantId);
      }
      return;
    }

    // FIX 2: shadow root may not be ready when MutationObserver fires —
    // retry with backoff up to 5 times (max ~3s total).
    const variantId = getVariantId(card);
    if (!variantId) {
      if (attempt < 5) setTimeout(() => tryInject(card, attempt + 1), 200 * (attempt + 1));
      return;
    }

    wrapAndInject(card, variantId);
  };

  const init = () => {
    document.querySelectorAll('br-product-card').forEach(card => tryInject(card));

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!node || node.nodeType !== 1 || SKIP_TAGS.has(node.tagName)) continue;
          if (node.tagName === 'BR-PRODUCT-CARD') { tryInject(node); continue; }
          node.querySelectorAll?.('br-product-card').forEach(card => tryInject(card));
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
